import { useEffect, useState } from "react";
import { Check, Loader2, PackageCheck, Undo2, X } from "lucide-react";
import { toast } from "sonner";
import { api, fmt, formatApiError } from "../../lib/api";
import { StatusBadge } from "../../components/StatusBadge";

export default function ReturnsAdminPage() {
  const [returns, setReturns] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/admin/returns").then((r) => setReturns(r.data.returns));
  useEffect(() => { load(); }, []);

  const act = async (rmaId, action) => {
    setBusy(`${rmaId}-${action}`);
    try {
      if (action === "reject") {
        const reason = window.prompt("Rejection reason:", "Item not eligible under return policy");
        if (reason === null) { setBusy(null); return; }
        await api.post(`/admin/returns/${rmaId}/reject`, { reason });
      } else {
        await api.post(`/admin/returns/${rmaId}/${action}`);
      }
      toast.success(`Return ${rmaId}: ${action} done — customer emailed`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setBusy(null);
  };

  const Btn = ({ rmaId, action, icon: Icon, label, cls }) => (
    <button
      data-testid={`rma-${action}-${rmaId}`}
      onClick={() => act(rmaId, action)}
      disabled={busy === `${rmaId}-${action}`}
      className={`flex items-center gap-1 rounded-lg border px-3 py-1.5 text-[11px] font-semibold disabled:opacity-50 ${cls}`}
    >
      {busy === `${rmaId}-${action}` ? <Loader2 size={11} className="animate-spin" /> : <Icon size={11} />} {label}
    </button>
  );

  return (
    <div data-testid="admin-returns-page">
      <div className="mb-6">
        <p className="eyebrow">RMA</p>
        <h1 className="font-heading text-2xl font-bold text-white">Returns management</h1>
        <p className="mt-1 text-xs text-slate-500">Approve or reject, mark items received, then issue the Stripe refund to the original payment method. Customers are emailed at every step.</p>
      </div>
      <div className="space-y-4">
        {returns.map((r) => (
          <div key={r.id} className="card-surface p-5" data-testid={`rma-card-${r.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-3">
                  <p className="font-mono font-bold text-amber-300">{r.id}</p>
                  <StatusBadge status={r.status} testid={`rma-status-${r.id}`} />
                </div>
                <p className="mt-1 text-xs text-slate-500">
                  Order <span className="font-mono text-slate-300">{r.order_number}</span> · {r.customer_name} · {r.email}
                </p>
              </div>
              <p className="font-heading text-lg font-bold text-white">{fmt(r.refund_amount)}</p>
            </div>
            <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-400">
              {r.items.map((i) => (
                <span key={i.product_id} className="rounded-full border border-white/10 px-3 py-1">{i.name} ×{i.qty}</span>
              ))}
              <span className="rounded-full border border-white/10 px-3 py-1">Reason: {r.reason}</span>
              {r.label && <span className="rounded-full border border-amber-400/30 px-3 py-1 font-mono text-amber-300">Label: {r.label.tracking_number}</span>}
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {r.status === "label_issued" && (
                <>
                  <Btn rmaId={r.id} action="approve" icon={Check} label="Approve" cls="border-emerald-400/40 text-emerald-300 hover:bg-emerald-400/10" />
                  <Btn rmaId={r.id} action="reject" icon={X} label="Reject" cls="border-rose-400/40 text-rose-300 hover:bg-rose-400/10" />
                </>
              )}
              {r.status === "requested" && (
                <Btn rmaId={r.id} action="reject" icon={X} label="Reject" cls="border-rose-400/40 text-rose-300 hover:bg-rose-400/10" />
              )}
              {r.status === "approved" && (
                <Btn rmaId={r.id} action="receive" icon={PackageCheck} label="Mark received & inspected" cls="border-amber-400/40 text-amber-300 hover:bg-amber-400/10" />
              )}
              {r.status === "received" && (
                <Btn rmaId={r.id} action="refund" icon={Undo2} label={`Refund ${fmt(r.refund_amount)} via Stripe`} cls="border-emerald-400/40 bg-emerald-400/10 text-emerald-300" />
              )}
            </div>
            <div className="mt-4 flex flex-wrap gap-x-5 gap-y-1 border-t border-white/[0.06] pt-3">
              {r.history?.map((h, i) => (
                <p key={i} className="text-[11px] text-slate-600">
                  <span className="text-slate-400">{h.status.replace(/_/g, " ")}</span> · {h.at.replace("T", " ").slice(0, 16)}
                </p>
              ))}
            </div>
          </div>
        ))}
        {returns.length === 0 && (
          <div className="card-surface p-12 text-center text-sm text-slate-500" data-testid="returns-empty">
            No return requests yet. Customers can file them from the storefront Returns page.
          </div>
        )}
      </div>
    </div>
  );
}
